import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiPlus, FiTrash2, FiUsers, FiX, FiEdit2, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { ConfirmModal, FormModal } from '../components/SharedModal';
import { ImSpinner2 } from 'react-icons/im';
import { getInternalGroups, createInternalGroup, deleteInternalGroup, InternalGroup, addContactToGroup, removeContactFromGroup, getContactsInGroup } from '../services/InternalGroupService';
import { getWhatsAppGroups, getWhatsAppGroupParticipants, WhatsAppGroupDto } from '../services/SectorService';
import { getContactsWithFilters, CONTACTS_PICKER_MAX_LIMIT } from '../services/ContactService';
import SessionService from '../services/SessionService';
import Toast, { ToastContainer } from '../components/Toast';
import { getDisplayPhone } from '../utils/formatters';
import '../styles/GroupsPage.css';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface ToastMessage {
    id: number;
    message: string;
    type: 'success' | 'error' | 'info';
}

interface Contact {
    id: number;
    name: string;
    number: string;
}

interface ManageGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    group: InternalGroup | null;
    onRefresh: () => void;
}

// Memoized contact item to prevent unnecessary re-renders
const ContactItem = React.memo<{
    contact: Contact;
    isSelected: boolean;
    onToggle: (id: number) => void;
}>(({ contact, isSelected, onToggle }) => (
    <label
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px',
            backgroundColor: isSelected ? 'rgba(37, 211, 102, 0.1)' : 'var(--bg-tertiary)',
            borderRadius: '6px',
            cursor: 'pointer',
            border: isSelected ? '1px solid #25D366' : '1px solid transparent'
        }}
    >
        <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(contact.id)}
            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
        />
        <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: '14px' }}>{contact.name}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{getDisplayPhone(contact.number)}</div>
        </div>
    </label>
));

const ManageGroupModal: React.FC<ManageGroupModalProps> = ({ isOpen, onClose, group, onRefresh }) => {
    const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
    const [groupMembers, setGroupMembers] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);

    const CONTACTS_PER_PAGE = 20;

    useEffect(() => {
        if (isOpen && group) {
            loadData();
            setSearchTerm('');
            setCurrentPage(0);
        }
    }, [isOpen, group]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const sectorId = SessionService.getSectorId();
            if (!sectorId || !group) return;

            // Load all contacts
            const contactsResponse = await getContactsWithFilters(sectorId, false, false, CONTACTS_PICKER_MAX_LIMIT, 0, 'picker');
            setAvailableContacts(contactsResponse.data || []);

            // Load group members
            const membersResponse = await getContactsInGroup(group.id);
            const memberIds = (membersResponse.data || []).map((c: Contact) => c.id);
            setGroupMembers(memberIds);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleMember = async (contactId: number) => {
        if (!group) return;

        try {
            if (groupMembers.includes(contactId)) {
                await removeContactFromGroup(group.id, contactId);
                setGroupMembers(prev => prev.filter(id => id !== contactId));
            } else {
                await addContactToGroup(group.id, contactId);
                setGroupMembers(prev => [...prev, contactId]);
            }
        } catch (error) {
            console.error('Error toggling member:', error);
        }
    };

    // Memoize filtered contacts to avoid recalculating on every render
    const filteredContacts = React.useMemo(() => {
        if (!searchTerm) return availableContacts;
        const term = searchTerm.toLowerCase();
        return availableContacts.filter(c =>
            c.name.toLowerCase().includes(term) ||
            c.number.includes(searchTerm)
        );
    }, [availableContacts, searchTerm]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE);
    const startIndex = currentPage * CONTACTS_PER_PAGE;
    const endIndex = startIndex + CONTACTS_PER_PAGE;
    const paginatedContacts = filteredContacts.slice(startIndex, endIndex);

    // Reset to page 0 when search changes
    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(0);
    };

    if (!isOpen || !group) return null;

    return ReactDOM.createPortal(
        <div className="groups-modal-overlay" onClick={onClose}>
            <div
                className="groups-modal"
                style={{ maxWidth: '600px', maxHeight: '80vh' }}
                onClick={e => e.stopPropagation()}
            >
                <div className="groups-modal-header">
                    <h2>Membros de "{group.name}"</h2>
                    <button className="groups-modal-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>
                <div className="groups-modal-content" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <div className="groups-form-group">
                        <label>Buscar contatos</label>
                        <input
                            type="text"
                            placeholder="Buscar por nome ou número..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>

                    {isLoading ? (
                        <div className="groups-loading">
                            <LoadingSpinner size="md" color="primary" />
                            <span className="groups-loading-text">Carregando contatos...</span>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '15px' }}>
                                {paginatedContacts.map(contact => (
                                    <ContactItem
                                        key={contact.id}
                                        contact={contact}
                                        isSelected={groupMembers.includes(contact.id)}
                                        onToggle={toggleMember}
                                    />
                                ))}
                                {filteredContacts.length === 0 && (
                                    <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                                        Nenhum contato encontrado
                                    </p>
                                )}
                            </div>

                            {totalPages > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '16px',
                                    paddingTop: '16px',
                                    borderTop: '1px solid var(--border-light)',
                                    fontSize: '13px',
                                    color: 'var(--text-muted)'
                                }}>
                                    <span>
                                        Mostrando {startIndex + 1}-{Math.min(endIndex, filteredContacts.length)} de {filteredContacts.length}
                                    </span>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                                            disabled={currentPage === 0}
                                            style={{
                                                padding: '6px 12px',
                                                border: '1px solid var(--border-light)',
                                                borderRadius: '6px',
                                                cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                                                opacity: currentPage === 0 ? 0.5 : 1,
                                                backgroundColor: 'var(--bg-card)',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Anterior
                                        </button>
                                        <span style={{ fontSize: '13px', fontWeight: 500 }}>
                                            Página {currentPage + 1} de {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                                            disabled={currentPage >= totalPages - 1}
                                            style={{
                                                padding: '6px 12px',
                                                border: '1px solid var(--border-light)',
                                                borderRadius: '6px',
                                                cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                                                opacity: currentPage >= totalPages - 1 ? 0.5 : 1,
                                                backgroundColor: 'var(--bg-card)',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="groups-modal-footer">
                    <button className="groups-save-button" onClick={() => { onRefresh(); onClose(); }}>
                        Concluir ({groupMembers.length} {groupMembers.length === 1 ? 'membro' : 'membros'})
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const GroupsPage: React.FC = () => {
    const [groups, setGroups] = useState<InternalGroup[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [manageModalOpen, setManageModalOpen] = useState<InternalGroup | null>(null);

    // Expansion state and members
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const [groupMembersData, setGroupMembersData] = useState<{ [groupId: number]: Contact[] }>({});
    const [loadingMembers, setLoadingMembers] = useState<{ [groupId: number]: boolean }>({});
    const [memberSearchTerms, setMemberSearchTerms] = useState<{ [groupId: number]: string }>({});
    const [memberPages, setMemberPages] = useState<{ [groupId: number]: number }>({});

    const MEMBERS_PER_PAGE = 5;

    // WhatsApp Groups State
    const [whatsappGroups, setWhatsappGroups] = useState<WhatsAppGroupDto[]>([]);
    const [whatsappGroupParticipants, setWhatsappGroupParticipants] = useState<{ [groupId: number]: any[] }>({});
    const [loadingWhatsappParticipants, setLoadingWhatsappParticipants] = useState<{ [groupId: number]: boolean }>({});
    const [expandedWhatsappGroups, setExpandedWhatsappGroups] = useState<Set<number>>(new Set());

    useEffect(() => {
        const sectorId = SessionService.getSectorId();
        if (sectorId) {
            fetchGroups();
            fetchWhatsAppGroups();
        } else {
            setIsLoading(false);
        }
    }, [SessionService.getSectorId()]);

    const fetchGroups = async () => {
        const sectorId = SessionService.getSectorId();
        if (!sectorId) return;

        try {
            setIsLoading(true);
            const response = await getInternalGroups(sectorId);
            setGroups(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching groups:', error);
            setGroups([]);
            addToast('Não foi possível carregar os grupos', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchWhatsAppGroups = async () => {
        const sectorId = SessionService.getSectorId();
        if (!sectorId) return;

        try {
            const response = await getWhatsAppGroups(sectorId);
            setWhatsappGroups(Array.isArray(response.data) ? response.data : []);
            console.log('[GroupsPage] WhatsApp groups loaded:', response.data?.length || 0);
        } catch (error) {
            console.error('Error fetching WhatsApp groups:', error);
            setWhatsappGroups([]);
        }
    };

    const toggleGroupExpansion = async (groupId: number) => {
        const newExpanded = new Set(expandedGroups);

        if (expandedGroups.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
            // Load members if not loaded
            if (!groupMembersData[groupId]) {
                await loadGroupMembers(groupId);
            }
        }

        setExpandedGroups(newExpanded);
    };

    const loadGroupMembers = async (groupId: number) => {
        setLoadingMembers(prev => ({ ...prev, [groupId]: true }));
        try {
            const response = await getContactsInGroup(groupId);
            setGroupMembersData(prev => ({ ...prev, [groupId]: response.data || [] }));
            setMemberPages(prev => ({ ...prev, [groupId]: 0 }));
        } catch (error) {
            console.error('Error loading group members:', error);
            setGroupMembersData(prev => ({ ...prev, [groupId]: [] }));
        } finally {
            setLoadingMembers(prev => ({ ...prev, [groupId]: false }));
        }
    };

    const getFilteredMembers = (groupId: number) => {
        const members = groupMembersData[groupId] || [];
        const searchTerm = memberSearchTerms[groupId] || '';

        if (!searchTerm) return members;

        return members.filter(m =>
            m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.number.includes(searchTerm)
        );
    };

    const getPaginatedMembers = (groupId: number) => {
        const filtered = getFilteredMembers(groupId);
        const page = memberPages[groupId] || 0;
        const start = page * MEMBERS_PER_PAGE;
        const end = start + MEMBERS_PER_PAGE;

        return {
            members: filtered.slice(start, end),
            totalPages: Math.ceil(filtered.length / MEMBERS_PER_PAGE),
            currentPage: page,
            total: filtered.length
        };
    };

    const addToast = (message: string, type: 'success' | 'error' | 'info') => {
        const newToast = {
            id: Date.now(),
            message,
            type,
        };
        setToasts(current => [...current, newToast]);
    };

    const removeToast = (id: number) => {
        setToasts(current => current.filter(toast => toast.id !== id));
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) {
            setError('O nome do grupo é obrigatório');
            return;
        }

        if (newGroupName.length < 2) {
            setError('O nome deve ter pelo menos 2 caracteres');
            return;
        }

        const sectorId = SessionService.getSectorId();
        if (!sectorId) {
            addToast('Selecione uma conexão para continuar', 'error');
            return;
        }

        try {
            setIsSaving(true);
            await createInternalGroup(newGroupName, sectorId);
            addToast('Grupo criado.', 'success');
            setNewGroupName('');
            setError(undefined);
            setShowModal(false);
            fetchGroups();
        } catch (error) {
            console.error('Error creating group:', error);
            setError('Não foi possível criar o grupo. Tente novamente.');
            addToast('Não foi possível criar o grupo', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteGroup = async (index: number) => {
        setIsDeleting(true);
        try {
            await deleteInternalGroup(groups[index].id);
            await fetchGroups();
            setDeleteModalOpen(null);
            addToast('Grupo excluído.', 'success');
        } catch (error) {
            console.error('Error deleting group:', error);
            addToast('Não foi possível excluir o grupo', 'error');
        }
        setIsDeleting(false);
    };

    const handleOpenModal = () => {
        setNewGroupName('');
        setError(undefined);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setNewGroupName('');
        setError(undefined);
    };

    return (
        <div className="groups-page-wrapper">
            <div className="groups-container">
                <div className="groups-header">
                    <div className="groups-header-content">
                        <h1>Grupos do WhatsApp</h1>
                        <p className="groups-header-description">Veja os grupos sincronizados do WhatsApp e seus participantes</p>
                    </div>
                    <button
                        className="groups-add-button"
                        onClick={handleOpenModal}
                        disabled={!SessionService.getSectorId()}
                    >
                        <FiPlus size={20} />
                        Novo grupo
                    </button>
                </div>

                <div className="groups-content">
                    {isLoading && SessionService.getSectorId() ? (
                        <div className="groups-loading">
                            <LoadingSpinner size="lg" color="primary" />
                            <span className="groups-loading-text">Carregando grupos...</span>
                        </div>
                    ) : !SessionService.getSectorId() ? (
                        <div className="groups-no-sector">
                            <h3>Nenhuma conexão selecionada</h3>
                            <p>Selecione uma conexão para ver os grupos</p>
                        </div>
                    ) : whatsappGroups.length === 0 ? (
                        <div className="groups-empty">
                            <div className="groups-empty-icon">
                                <FiUsers />
                            </div>
                            <h3 className="groups-empty-title">Nenhum grupo do WhatsApp encontrado</h3>
                            <p className="groups-empty-description">Sincronize seus contatos para ver os grupos do WhatsApp aqui</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {whatsappGroups.map((group) => {
                                const isExpanded = expandedWhatsappGroups.has(group.id);

                                return (
                                    <div
                                        key={`wa-${group.id}`}
                                        style={{
                                            backgroundColor: 'var(--bg-card)',
                                            borderRadius: '12px',
                                            border: '1px solid var(--border-light)',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Group Header */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '16px 20px',
                                                gap: '12px'
                                            }}
                                        >
                                            <button
                                                onClick={async () => {
                                                    const newExpanded = new Set(expandedWhatsappGroups);
                                                    if (isExpanded) {
                                                        newExpanded.delete(group.id);
                                                    } else {
                                                        newExpanded.add(group.id);
                                                        // Carregar participantes se ainda não foram carregados
                                                        if (!whatsappGroupParticipants[group.id]) {
                                                            const sectorId = SessionService.getSectorId();
                                                            if (sectorId) {
                                                                setLoadingWhatsappParticipants(prev => ({ ...prev, [group.id]: true }));
                                                                try {
                                                                    const response = await getWhatsAppGroupParticipants(sectorId, group.whatsappGroupId ?? group.id);
                                                                    setWhatsappGroupParticipants(prev => ({ ...prev, [group.id]: response.data || [] }));
                                                                } catch (error) {
                                                                    console.error('Error loading participants:', error);
                                                                    setWhatsappGroupParticipants(prev => ({ ...prev, [group.id]: [] }));
                                                                } finally {
                                                                    setLoadingWhatsappParticipants(prev => ({ ...prev, [group.id]: false }));
                                                                }
                                                            }
                                                        }
                                                    }
                                                    setExpandedWhatsappGroups(newExpanded);
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: 'var(--text-muted)',
                                                    padding: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'transform 0.2s'
                                                }}
                                            >
                                                {isExpanded ? <FiChevronDown size={20} /> : <FiChevronRight size={20} />}
                                            </button>

                                            <FiUsers size={20} color="#25D366" />

                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--text-primary)' }}>
                                                    {group.name}
                                                </div>
                                                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                                    {group.participantCount} {group.participantCount === 1 ? 'participante' : 'participantes'}
                                                </div>
                                            </div>

                                            <div style={{
                                                padding: '6px 12px',
                                                backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                color: '#25D366'
                                            }}>
                                                Sincronizado
                                            </div>
                                        </div>

                                        {/* Participants Section */}
                                        {isExpanded && (
                                            <div
                                                style={{
                                                    borderTop: '1px solid var(--border-light)',
                                                    padding: '16px 20px',
                                                    backgroundColor: 'var(--bg-tertiary)'
                                                }}
                                            >
                                                {loadingWhatsappParticipants[group.id] ? (
                                                    <div className="groups-loading" style={{ minHeight: '100px' }}>
                                                        <LoadingSpinner size="md" color="primary" />
                                                        <span className="groups-loading-text">Carregando participantes...</span>
                                                    </div>
                                                ) : whatsappGroupParticipants[group.id] && whatsappGroupParticipants[group.id].length > 0 ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {whatsappGroupParticipants[group.id].map((participant: any, index: number) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '12px 14px',
                                                                    backgroundColor: 'var(--bg-card)',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid var(--border-light)',
                                                                    gap: '12px'
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: '40px',
                                                                        height: '40px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: (() => {
                                                                            const colors = ['#FFADAD', '#FFD6A5', '#FDFFB6', '#CAFFBF', '#9BF6FF', '#A0C4FF', '#BDB2FF', '#FFC6FF'];
                                                                            let hash = 0;
                                                                            const str = participant.phone_number || participant.jid || '';
                                                                            for (let i = 0; i < str.length; i++) {
                                                                                hash = str.charCodeAt(i) + ((hash << 5) - hash);
                                                                            }
                                                                            return colors[Math.abs(hash) % colors.length];
                                                                        })(),
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: 'var(--text-primary)',
                                                                        fontSize: '16px',
                                                                        fontWeight: 600,
                                                                        flexShrink: 0,
                                                                        overflow: 'hidden'
                                                                    }}
                                                                >
                                                                    {participant.avatar_url ? (
                                                                        <img
                                                                            src={participant.avatar_url}
                                                                            alt={participant.name}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                            onError={(e) => {
                                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        (participant.name || participant.phone_number || participant.jid || '?').substring(0, 1).toUpperCase()
                                                                    )}
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div
                                                                        title={participant.phone_number ? `${participant.phone_number} (${participant.jid})` : participant.jid}
                                                                        style={{
                                                                            fontWeight: 500,
                                                                            fontSize: '14px',
                                                                            color: 'var(--text-primary)',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                    >
                                                                        {participant.name || getDisplayPhone(participant.phone_number || participant.jid) || participant.jid.split('@')[0]}
                                                                    </div>
                                                                    {participant.name && (participant.phone_number || (participant.jid && participant.name !== participant.jid)) && (
                                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                                            {getDisplayPhone(participant.phone_number || participant.jid) || participant.jid.split('@')[0]}
                                                                        </div>
                                                                    )}
                                                                    {participant.isAdmin && (
                                                                        <div style={{
                                                                            fontSize: '12px',
                                                                            color: '#25D366',
                                                                            marginTop: '2px',
                                                                            fontWeight: 500
                                                                        }}>
                                                                            Admin
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                                                        Nenhum participante encontrado.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
            </div>
        </div>
            <FormModal
                open={showModal}
                onClose={handleCloseModal}
                onSubmit={handleCreateGroup}
                title="Novo grupo"
                submitLabel="Criar"
                cancelLabel="Cancelar"
                loading={isSaving}
                submitDisabled={!newGroupName.trim()}
            >
                <div className="shared-modal__form-group">
                    <label htmlFor="group-name">Nome <span className="required">*</span></label>
                    <input
                        id="group-name"
                        type="text"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        placeholder="Nome do grupo"
                        className={error ? 'error' : ''}
                        autoFocus
                    />
                    {error && <span className="shared-modal__error">{error}</span>}
                </div>
            </FormModal>
            <ConfirmModal
                open={deleteModalOpen !== null}
                onClose={() => { if (!isDeleting) setDeleteModalOpen(null); }}
                onConfirm={() => { if (deleteModalOpen !== null && !isDeleting) handleDeleteGroup(deleteModalOpen); }}
                title="Confirmar exclusão"
                message={deleteModalOpen !== null ? `Excluir o grupo "${groups[deleteModalOpen]?.name || ''}"?` : ''}
                confirmLabel="Excluir"
                cancelLabel="Cancelar"
                variant="danger"
                loading={isDeleting}
            />
            <ToastContainer>
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </ToastContainer>
        </div>
    );
};

export default GroupsPage;
